import {
  LayoutNode,
  TableLayoutProps,
} from 'react-stately/useVirtualizerState';

import { LayoutInfo, Rect, Size, TableLayout } from 'react-aria-components';
import { CollectionNode } from 'react-aria/private/collections/BaseCollection';
import { GridNode } from '@react-types/grid';
import { getChildNodes } from 'react-stately/private/collections/getChildNodes';
import { SapphireTableCollection } from './SapphireTableCollection';

export class SapphireTableLayout<T> extends TableLayout<T, TableLayoutProps> {
  protected override isStickyColumn(node: GridNode<T>): boolean {
    return node.props.isSticky;
  }

  override buildCollection(): Array<LayoutNode> {
    const nodes = super.buildCollection();
    const footer = this.buildRowGroup(
      (this.collection as SapphireTableCollection<T>).footer,
      nodes[nodes.length - 1]?.layoutInfo.rect.maxY ?? 0
    );
    if (footer.children?.length) {
      this.contentSize = new Size(
        this.contentSize.width,
        footer.layoutInfo.rect.maxY
      );
      return [...nodes, footer];
    }
    return nodes;
  }

  protected override buildBody(y: number): LayoutNode {
    const layoutNode = super.buildBody(y);
    // Needs overflow for sticky cells
    const { layoutInfo } = layoutNode;
    layoutInfo.allowOverflow = true;
    if (this.virtualizer?.collection.size === 0) {
      layoutInfo.rect.height = 5;
    }
    return layoutNode;
  }

  protected override buildRow(
    node: GridNode<T>,
    x: number,
    y: number
  ): LayoutNode {
    const layoutNode = super.buildRow(node, x, y);
    layoutNode.layoutInfo.allowOverflow = true;
    // Needs overflow for sticky cells
    return layoutNode;
  }

  protected override buildTableHeader(): LayoutNode {
    const layoutNode = super.buildTableHeader();
    // Needs overflow for sticky columns
    layoutNode.layoutInfo.allowOverflow = true;
    return layoutNode;
  }

  /**
   * This is corresponding to ListLayout.buildSection, and almost a copy of buildBody,
   * with the following differences:
   * - Instead of hard coding table.body, it accepts the row group as input
   * - When adding height of the skipped nodes, it uses childNodes.length instead of
   * collection.size. It's more correct, and matches the logic in ListLayout.buildSection.
   */

  protected buildRowGroup(
    rowgroupNode: CollectionNode<T>,
    y: number
  ): LayoutNode {
    const collection = this.virtualizer!
      .collection as SapphireTableCollection<T>;
    const rect = new Rect(this.padding, y, 0, 0);
    const layoutInfo = new LayoutInfo('rowgroup', rowgroupNode.key, rect);

    const startY = y;
    let skipped = 0;
    let width = 0;
    const children: LayoutNode[] = [];
    const rowHeight = this.getEstimatedRowHeight() + this.gap;
    const childNodes = [...getChildNodes(rowgroupNode, collection)];
    for (const node of childNodes) {
      // Skip rows before the valid rectangle unless they are already cached.
      if (y + rowHeight < this.requestedRect.y && !this.isValid(node, y)) {
        y += rowHeight;
        skipped++;
        continue;
      }

      const layoutNode = this.buildChild(node, this.padding, y, layoutInfo.key);
      layoutNode.layoutInfo.parentKey = layoutInfo.key;
      layoutNode.index = children.length;
      y = layoutNode.layoutInfo.rect.maxY + this.gap;
      width = Math.max(width, layoutNode.layoutInfo.rect.width);
      children.push(layoutNode);

      if (y > this.requestedRect.maxY) {
        // Estimate the remaining height for rows that we don't need to layout right now.
        y += (childNodes.length - (children.length + skipped)) * rowHeight;
        break;
      }
    }

    if (children.length === 0) {
      y = this.virtualizer!.visibleRect.maxY;
    } else {
      y -= this.gap;
    }

    rect.width = width;
    rect.height = y - startY;

    return {
      layoutInfo,
      children,
      validRect: layoutInfo.rect.intersection(this.requestedRect),
      node: collection.body,
    };
  }
}
